import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Client {
  id: string;
  name: string;
  logo?: string;
  industry: string;
  description: string;
}

const ClientsSection: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const clients: Client[] = [
    {
      id: '1',
      name: 'European Commission',
      logo: '/assets/images/ec-logo-horiz_en.png',
      industry: t('clients.client1.industry'),
      description: t('clients.client1.description')
    },
    {
      id: '2',
      name: 'CANCOM',
      logo: '/assets/images/Cancom_logo.png',
      industry: t('clients.client2.industry'),
      description: t('clients.client2.description')
    },
    {
      id: '3',
      name: 'United Nations',
      logo: '/assets/images/UN-United-Nations-Emblem.png',
      industry: t('clients.client3.industry'),
      description: t('clients.client3.description')
    },
    {
      id: '4',
      name: 'Socialoop',
      logo: '/assets/images/SLlogo-removebg-preview.png',
      industry: t('clients.client4.industry'),
      description: t('clients.client4.description')
    },
    {
      id: '5',
      name: 'limango',
      logo: '/assets/images/limango_logo_472x192px.webp',
      industry: t('clients.client5.industry'),
      description: t('clients.client5.description')
    },
    {
      id: '6',
      name: 'Natuzzi',
      logo: '/assets/images/Natuzzi-Logo.png',
      industry: t('clients.client6.industry'),
      description: t('clients.client6.description')
    }
  ];

  const industries = [
    { name: t('clients.industries.fintech'), count: 8, color: 'from-blue-500 to-cyan-500' },
    { name: t('clients.industries.healthcare'), count: 5, color: 'from-green-500 to-emerald-500' },
    { name: t('clients.industries.ecommerce'), count: 12, color: 'from-purple-500 to-pink-500' },
    { name: t('clients.industries.manufacturing'), count: 6, color: 'from-orange-500 to-red-500' },
    { name: t('clients.industries.education'), count: 4, color: 'from-indigo-500 to-blue-500' },
    { name: t('clients.industries.logistics'), count: 7, color: 'from-yellow-500 to-orange-500' }
  ];

  return (
    <section ref={ref} className="space-y-8">
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
          {t('clients.title')}
        </h2>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto">
          {t('clients.subtitle')}
        </p>
      </motion.header>

      {/* Client Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {clients.map((client, index) => (
          <motion.div
            key={client.id}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 * index }}
            whileHover={{ scale: 1.02 }}
            className="group"
          >
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 h-full transition-all duration-300 hover:border-gray-600 hover:shadow-lg relative">
              {/* Client Logo */}
              <div className="w-32 h-16 bg-white rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300 p-2">
                {client.logo ? (
                  <img
                    src={client.logo}
                    alt={`${client.name} logo`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-400 to-secondary-400 rounded-md flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {client.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>

              {/* Client Info */}
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-white group-hover:text-primary-300 transition-colors">
                  {client.name}
                </h3>
                
                <div className="inline-block px-3 py-1 bg-primary-500/10 border border-primary-500/20 rounded-full">
                  <span className="text-primary-300 text-sm font-medium">
                    {client.industry}
                  </span>
                </div>
                
                <p className="text-gray-300 text-sm leading-relaxed">
                  {client.description}
                </p>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-300 pointer-events-none" />
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Industries Overview */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-gray-800 border border-gray-700 rounded-2xl p-8"
      >
        <h3 className="text-2xl font-bold text-white mb-6 text-center">
          {t('clients.industriesTitle')}
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {industries.map((industry, index) => (
            <motion.div
              key={industry.name}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="text-center group cursor-pointer"
            >
              <div className={`w-16 h-16 mx-auto mb-3 rounded-xl bg-gradient-to-br ${industry.color} p-4 group-hover:scale-110 transition-transform duration-300`}>
                <div className="w-full h-full bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {industry.count}
                  </span>
                </div>
              </div>
              <h4 className="text-white font-medium text-sm mb-1 group-hover:text-primary-300 transition-colors">
                {industry.name}
              </h4>
              <p className="text-gray-400 text-xs">
                {t('clients.projectsCompleted')}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Partnership CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="text-center"
      >
        <div className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/20 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4">
            {t('clients.partnershipTitle')}
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            {t('clients.partnershipDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary px-8 py-3">
              {t('clients.startProject')}
            </button>
            <button className="btn-secondary px-8 py-3">
              {t('clients.viewPortfolio')}
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ClientsSection;